import styled from 'styled-components';

import { TextInput } from './formControls';
import LanguageDropdown from './LanguageDropdown';

import type { LanguageRef } from '@src/repositories/TranslationMemoryEntryRepository';

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  /** Source / base-language filter value (`''` = all source languages). */
  sourceFilter: string;
  onSourceChange: (value: string) => void;
  sourceLanguages: LanguageRef[];
  /** Target-language filter value (`''` = all target languages). */
  targetFilter: string;
  onTargetChange: (value: string) => void;
  targetLanguages: LanguageRef[];
  /** Hidden in matrix mode, where every target language is already a column. */
  showTargetFilter: boolean;
  /** The "All source languages" option is dropped in matrix mode, where a
   *  concrete base language must sit on the left. */
  showAllSourceOption: boolean;
  /** "Source language" in table mode, "Base language" in matrix mode. */
  sourceLabel: string;
}

/**
 * The shared filter/search bar over the Translation Memory view (PLAT-10846):
 * a compact source/base-language picker + target-language picker + free-text
 * search, all on one streamlined row (no stacked field labels — the globe/code
 * triggers and the search placeholder are self-describing). Search flexes to
 * fill the slack and shrinks first so the whole toolbar stays on a single line.
 * The language pickers reuse the Transcript & Speakers dropdown pattern.
 */
const FilterBar = ({
  search,
  onSearchChange,
  sourceFilter,
  onSourceChange,
  sourceLanguages,
  targetFilter,
  onTargetChange,
  targetLanguages,
  showTargetFilter,
  showAllSourceOption,
  sourceLabel,
}: Props) => (
  <Bar>
    <LanguageDropdown
      propertyName="tmSourceLanguage"
      ariaLabel={sourceLabel}
      value={sourceFilter}
      onChange={onSourceChange}
      languages={sourceLanguages}
      allLabel={showAllSourceOption ? 'All source languages' : undefined}
    />
    {showTargetFilter && (
      <LanguageDropdown
        propertyName="tmTargetLanguage"
        ariaLabel="Target language"
        value={targetFilter}
        onChange={onTargetChange}
        languages={targetLanguages}
        allLabel="All target languages"
      />
    )}
    <SearchInput
      type="search"
      aria-label="Search"
      value={search}
      placeholder="Search terms, translations, comments"
      onChange={e => onSearchChange(e.target.value)}
    />
  </Bar>
);

export default FilterBar;

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  min-width: 0;
`;

// Search takes the slack and is the only element that shrinks, so the pickers
// stay legible and the row never wraps.
const SearchInput = styled(TextInput)`
  flex: 1;
  min-width: 0;
`;
